import { memo } from 'react';
import { Button, Badge, useToast } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { Copy, KeyRound, Unplug, Clock, User } from 'lucide-react';
import IntegrationProviderLogo from './IntegrationProviderLogo.jsx';
import StatusPill from './StatusPill.jsx';
import AuthExpiryBanner from './AuthExpiryBanner.jsx';
import { useIntegrationsPermissions } from '../hooks';
import { formatRelativeTime } from '../utils';

// Prototype `.btn-danger` (ghost-danger): tinted surface, red text/border — not solid fill.
const DISCONNECT_CLASS = [
  '!border !border-red-500/20 !bg-red-500/10 !text-red-400',
  'hover:!bg-red-500/18 hover:!text-red-300',
].join(' ');

// Compact instance-id chip next to the title (prototype ID pill).
const ID_PILL_CLASS = [
  'inline-flex items-center gap-1 rounded-lg border border-white/[0.06] bg-white/[0.03]',
  'px-2 py-0.5 font-mono text-[11px] font-medium text-content-secondary',
].join(' ');

const META_PILL_CLASS = [
  'inline-flex items-center gap-[5px] rounded-lg border border-white/[0.06] bg-white/[0.03]',
  'px-[9px] py-1 text-[11.5px] font-medium text-content-secondary',
].join(' ');

function MetaPill({ icon: Icon, children }) {
  return (
    <span className={META_PILL_CLASS}>
      <Icon size={11} aria-hidden="true" className="shrink-0 text-content-faint" />
      {children}
    </span>
  );
}

/**
 * Instance Detail sticky header (CT-4188): identity + instance-level health +
 * admin actions. Shows the INSTANCE status (not the provider aggregate), a
 * copyable instance ID, the AIMS-managed/BYOK chip, auth chip, last run, and
 * Rotate / Disconnect actions gated by `canManageIntegrations`. Auth-expiry
 * banner sits above the identity row. Stickiness is owned by InstanceDetailLayout
 * so the header + tabs share one sticky containing block (the page scroller).
 */
function InstanceHeader({ instance, onRotate, onDisconnect }) {
  const { t } = useTranslation('admin-studio');
  const toast = useToast();
  const { canManageIntegrations } = useIntegrationsPermissions();

  const showAuthBanner = instance.status === 'auth_expired' || instance.status === 'auth_expiring';

  const copyId = async () => {
    try {
      await navigator.clipboard?.writeText(instance.instanceId);
      toast?.success?.(t('integrations.instance.header.idCopied'));
    } catch {
      toast?.error?.(t('common.requestFailed'));
    }
  };

  return (
    <div className="flex flex-col gap-3">
      {showAuthBanner && (
        <AuthExpiryBanner
          status={instance.status}
          credentialExpiresAt={instance.credentialExpiresAt}
          onRotate={onRotate}
          canManage={canManageIntegrations}
        />
      )}

      <div>
        <div className="flex flex-wrap items-start gap-4">
          <IntegrationProviderLogo
            slug={instance.providerSlug}
            name={instance.providerName}
            size="large"
          />

          <div className="flex min-w-0 flex-1 flex-col gap-[5px]">
            <div className="flex flex-wrap items-center gap-[9px]">
              <h1 className="text-2xl font-bold leading-[1.2] tracking-[-0.015em] text-content">
                {instance.name}
              </h1>
              <span className={ID_PILL_CLASS}>
                {instance.instanceId}
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={Copy}
                  onClick={copyId}
                  aria-label={t('integrations.instance.header.copyId')}
                  className="!h-auto !min-h-0 !p-0.5"
                />
              </span>
              {instance.official ? (
                <Badge variant="informative" size="small" radius="full">
                  {t('integrations.instance.header.official')}
                </Badge>
              ) : null}
            </div>

            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              <StatusPill status={instance.status} activeVariant surface="detail" />
              <Badge variant="neutral" size="small" radius="full">
                {t(`integrations.instance.managed.${instance.managedBy}`)}
              </Badge>
              {instance.authMethod ? (
                <Badge variant="neutral" size="small" radius="full">
                  {t(`integrations.provider.auth.${instance.authMethod}`)}
                </Badge>
              ) : null}
              {instance.lastRun ? (
                <MetaPill icon={Clock}>
                  {t('integrations.instance.header.lastRun', {
                    time: formatRelativeTime(instance.lastRun, t),
                  })}
                </MetaPill>
              ) : null}
              {instance.connectedBy ? (
                <MetaPill icon={User}>
                  {t('integrations.instance.header.connectedBy', {
                    user: instance.connectedBy.name,
                  })}
                  {instance.connectedAt ? (
                    <>
                      {' · '}
                      <b className="font-semibold text-content">
                        {formatRelativeTime(instance.connectedAt, t)}
                      </b>
                    </>
                  ) : null}
                </MetaPill>
              ) : null}
            </div>
          </div>

          {canManageIntegrations && (
            <div className="flex flex-shrink-0 flex-wrap items-center gap-2">
              <Button variant="secondary" size="sm" leftIcon={KeyRound} onClick={onRotate}>
                {t('integrations.instance.header.rotate')}
              </Button>
              <Button
                variant="danger"
                size="sm"
                leftIcon={Unplug}
                onClick={onDisconnect}
                className={DISCONNECT_CLASS}
              >
                {t('integrations.instance.header.disconnect')}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default memo(InstanceHeader);
